import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Modal, Button, Form, Alert, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import html2canvas from 'html2canvas';
import {
  normalizeShortsStats,
  computeTripStatsTotals,
  formatSteps,
  formatKm,
  formatTripAvgCeil,
  countDayAttractions,
  getFunEmoji,
  getFatigueEmoji,
  FUN_EMOJI_ROW,
  FATIGUE_EMOJI_ROW,
  EMPTY_SHORTS_STATS,
} from '../shared/shortsStatsUtils';

const SHORTS_WIDTH = 1080;
const SHORTS_HEIGHT = 1920;
/** Same as Shorts map: preview pixels = export source; upscale on download for WYSIWYG. */
const PREVIEW_MAX_WIDTH = 320;

function sc(size, s) {
  return Math.round(size * s);
}

function EmojiScaleRow({ emojis, value, dense, s }) {
  const active =
    value != null ? Math.min(10, Math.max(0, Math.round(Number(value)))) : null;
  const baseSize = sc(dense ? 30 : 40, s);
  const onSize = sc(dense ? 48 : 60, s);
  const pastSize = sc(dense ? 34 : 44, s);
  const offSize = sc(dense ? 28 : 34, s);

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: sc(4, s),
        marginTop: sc(dense ? 12 : 16, s),
        padding: `0 ${sc(4, s)}px`,
      }}
    >
      {emojis.map((emoji, index) => {
        const isOn = active === index;
        const isPast = active != null && index <= active;
        return (
          <div
            key={index}
            style={{
              flex: 1,
              textAlign: 'center',
              fontSize: active == null ? baseSize : isOn ? onSize : isPast ? pastSize : offSize,
              lineHeight: 1.1,
              opacity: active == null ? 0.35 : isOn ? 1 : isPast ? 0.85 : 0.25,
              transform: isOn ? 'scale(1.15)' : 'scale(1)',
            }}
            title={String(index)}
          >
            {emoji}
          </div>
        );
      })}
    </div>
  );
}

function BigStatTile({ iconEmoji, iconBg, value, label, compact, tripSoFarText, dense, s }) {
  const iconSize = sc(compact ? 72 : dense ? 88 : 100, s);
  const valueSize = sc(compact ? 64 : dense ? 76 : 92, s);
  const labelSize = sc(compact ? 30 : dense ? 34 : 40, s);
  const padV = sc(compact ? 28 : dense ? 28 : 36, s);
  const padH = sc(compact ? 20 : dense ? 24 : 28, s);
  const minH = sc(compact ? 200 : dense ? 220 : 280, s);
  const parenSize = sc(compact ? 20 : dense ? 24 : 30, s);
  const radius = sc(compact ? 24 : 32, s);

  return (
    <div
      style={{
        flex: 1,
        backgroundColor: '#ffffff',
        borderRadius: radius,
        padding: `${padV}px ${padH}px`,
        boxShadow: '0 4px 20px rgba(15, 23, 42, 0.08)',
        border: `${Math.max(1, sc(2, s))}px solid #e2e8f0`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        minHeight: minH,
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          width: iconSize,
          height: iconSize,
          borderRadius: '50%',
          backgroundColor: iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: sc(compact ? 16 : 24, s),
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
        }}
      >
        <span style={{ fontSize: sc(compact ? 44 : 56, s), lineHeight: 1 }} role="img" aria-hidden>
          {iconEmoji}
        </span>
      </div>
      <div
        style={{
          fontSize: valueSize,
          fontWeight: 800,
          lineHeight: 1.15,
          letterSpacing: '-1px',
          color: '#212529',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'baseline',
          justifyContent: 'center',
          gap: `0 ${sc(10, s)}px`,
        }}
      >
        <span>{value}</span>
        {tripSoFarText ? (
          <span
            style={{
              fontSize: parenSize,
              fontWeight: 600,
              color: '#868e96',
              letterSpacing: 0,
            }}
          >
            {tripSoFarText}
          </span>
        ) : null}
      </div>
      <div
        style={{
          fontSize: labelSize,
          fontWeight: 700,
          color: '#495057',
          marginTop: sc(compact ? 8 : 12, s),
          textTransform: 'uppercase',
          letterSpacing: 1,
        }}
      >
        {label}
      </div>
    </div>
  );
}

function MoodBlock({ title, badgeEmoji, accent, bgColor, value, emojiRow, isAverage, compact, tripSoFarText, dense, s }) {
  const displayEmoji = value != null ? getFunEmoji(value) : '❔';
  const displayFatigueEmoji = value != null ? getFatigueEmoji(value) : '❔';
  const heroEmoji = emojiRow === FUN_EMOJI_ROW ? displayEmoji : displayFatigueEmoji;
  const heroSize = sc(compact ? 80 : dense ? 100 : 128, s);
  const titleSize = sc(compact ? 36 : dense ? 42 : 48, s);
  const scoreSize = sc(compact ? 30 : dense ? 44 : 52, s);
  const parenSize = sc(compact ? 22 : dense ? 28 : 34, s);
  const badgeBox = sc(compact ? 56 : 72, s);
  const borderW = Math.max(1, sc(3, s));

  return (
    <div
      style={{
        backgroundColor: bgColor,
        borderRadius: sc(compact ? 28 : 36, s),
        padding: compact
          ? `${sc(28, s)}px ${sc(28, s)}px ${sc(24, s)}px`
          : dense
            ? `${sc(32, s)}px ${sc(32, s)}px ${sc(28, s)}px`
            : `${sc(44, s)}px ${sc(40, s)}px ${sc(40, s)}px`,
        border: `${borderW}px solid ${accent}`,
        boxShadow: '0 4px 20px rgba(15, 23, 42, 0.06)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: sc(20, s),
          marginBottom: sc(compact ? 4 : 8, s),
        }}
      >
        <div
          style={{
            width: badgeBox,
            height: badgeBox,
            borderRadius: sc(compact ? 16 : 20, s),
            backgroundColor: accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: sc(compact ? 36 : 44, s), lineHeight: 1 }} role="img" aria-hidden>
            {badgeEmoji}
          </span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: titleSize, fontWeight: 800, color: '#212529', lineHeight: 1.1 }}>{title}</div>
          {isAverage ? (
            <div style={{ fontSize: sc(26, s), color: '#6c757d', marginTop: sc(4, s) }}>Trip average</div>
          ) : null}
        </div>
        <div
          style={{
            fontSize: heroSize,
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          {heroEmoji}
        </div>
      </div>
      {value != null ? (
        <div
          style={{
            fontSize: scoreSize,
            fontWeight: 700,
            color: accent,
            marginBottom: sc(4, s),
            lineHeight: 1.2,
          }}
        >
          {Math.round(value)}/10
          {tripSoFarText ? (
            <span
              style={{
                fontSize: parenSize,
                fontWeight: 600,
                color: '#868e96',
                marginLeft: sc(10, s),
              }}
            >
              {tripSoFarText}
            </span>
          ) : null}
        </div>
      ) : (
        <div style={{ fontSize: sc(28, s), color: '#adb5bd', marginBottom: sc(4, s) }}>
          Not set
          {tripSoFarText ? (
            <span style={{ fontSize: parenSize, marginLeft: sc(8, s), color: '#868e96' }}>{tripSoFarText}</span>
          ) : null}
        </div>
      )}
      {!compact ? <EmojiScaleRow emojis={emojiRow} value={value} dense={dense} s={s} /> : null}
    </div>
  );
}

function StatsSection({
  sectionTitle,
  sectionEmoji,
  sectionColor,
  stats,
  tripTotalsMode,
  attractionCount,
  t,
  showAttractions,
  compact,
  tripTotals,
  showTripSoFarParen,
  dense,
  s,
}) {
  const steps = tripTotalsMode ? formatSteps(stats.totalSteps) : formatSteps(stats.steps);
  const km = tripTotalsMode ? formatKm(stats.totalKm) : formatKm(stats.distanceKm);
  const funVal = tripTotalsMode ? stats.avgFun : stats.fun;
  const fatigueVal = tripTotalsMode ? stats.avgFatigue : stats.fatigue;

  const stepsTripParen =
    showTripSoFarParen && tripTotals?.totalSteps != null
      ? t('planDetail.days.shortsStats.tripSoFarParen', { value: formatSteps(tripTotals.totalSteps) })
      : null;
  const kmTripParen =
    showTripSoFarParen && tripTotals?.totalKm != null
      ? t('planDetail.days.shortsStats.tripSoFarParen', { value: formatKm(tripTotals.totalKm) })
      : null;
  const funTripParen =
    showTripSoFarParen && tripTotals?.avgFun != null
      ? t('planDetail.days.shortsStats.tripSoFarAvgParen', { value: formatTripAvgCeil(tripTotals.avgFun) })
      : null;
  const fatigueTripParen =
    showTripSoFarParen && tripTotals?.avgFatigue != null
      ? t('planDetail.days.shortsStats.tripSoFarAvgParen', { value: formatTripAvgCeil(tripTotals.avgFatigue) })
      : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: sc(compact ? 20 : dense ? 28 : 36, s) }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: sc(16, s) }}>
        <span style={{ fontSize: sc(compact ? 44 : 52, s), lineHeight: 1 }} role="img" aria-hidden>
          {sectionEmoji}
        </span>
        <span style={{ fontSize: sc(compact ? 44 : 52, s), fontWeight: 800, color: sectionColor }}>{sectionTitle}</span>
      </div>

      <div style={{ display: 'flex', gap: sc(compact ? 20 : 28, s) }}>
        <BigStatTile
          iconEmoji="🚶"
          iconBg="#0d6efd"
          value={steps}
          label={t('planDetail.days.shortsStats.steps')}
          compact={compact}
          tripSoFarText={stepsTripParen}
          dense={dense}
          s={s}
        />
        <BigStatTile
          iconEmoji="🗺️"
          iconBg="#198754"
          value={km}
          label={t('planDetail.days.shortsStats.km')}
          compact={compact}
          tripSoFarText={kmTripParen}
          dense={dense}
          s={s}
        />
      </div>

      {showAttractions && attractionCount > 0 && !tripTotalsMode ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: sc(16, s),
            fontSize: sc(36, s),
            fontWeight: 600,
            color: '#334155',
            backgroundColor: '#ffffff',
            borderRadius: sc(24, s),
            padding: `${sc(22, s)}px ${sc(32, s)}px`,
            boxShadow: '0 2px 12px rgba(15, 23, 42, 0.06)',
            border: `${Math.max(1, sc(1, s))}px solid #e2e8f0`,
          }}
        >
          <span style={{ fontSize: sc(40, s), lineHeight: 1 }} role="img" aria-hidden>
            📍
          </span>
          {t('planDetail.days.shortsStats.attractionsPlanned', { count: attractionCount })}
        </div>
      ) : null}

      <div style={{ display: 'flex', flexDirection: compact ? 'row' : 'column', gap: compact ? 20 : 0 }}>
        <div style={{ flex: 1 }}>
          <MoodBlock
            title={tripTotalsMode ? t('planDetail.days.shortsStats.avgFun') : t('planDetail.days.shortsStats.fun')}
            badgeEmoji="😊"
            accent="#e67700"
            bgColor="#fff9db"
            value={funVal}
            emojiRow={FUN_EMOJI_ROW}
            isAverage={tripTotalsMode}
            compact={compact}
            tripSoFarText={funTripParen}
            dense={dense}
            s={s}
          />
        </div>
        {!compact ? <div style={{ height: sc(dense ? 12 : 24, s) }} /> : null}
        <div style={{ flex: 1 }}>
          <MoodBlock
            title={
              tripTotalsMode ? t('planDetail.days.shortsStats.avgFatigue') : t('planDetail.days.shortsStats.fatigue')
            }
            badgeEmoji="🔋"
            accent="#6741d9"
            bgColor="#f3f0ff"
            value={fatigueVal}
            emojiRow={FATIGUE_EMOJI_ROW}
            isAverage={tripTotalsMode}
            compact={compact}
            tripSoFarText={fatigueTripParen}
            dense={dense}
            s={s}
          />
        </div>
      </div>
    </div>
  );
}

function StatsCard({
  frameRef,
  width,
  planName,
  dayTitle,
  totalDays,
  todayStats,
  tripTotals,
  attractionCount,
  t,
}) {
  const showTripParen = totalDays > 1;
  const s = width / SHORTS_WIDTH;
  const height = Math.round(width * (SHORTS_HEIGHT / SHORTS_WIDTH));

  return (
    <div
      ref={frameRef}
      style={{
        width,
        height,
        backgroundColor: '#e8eef5',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
        color: '#212529',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          padding: `${sc(56, s)}px ${sc(48, s)}px ${sc(56, s)}px`,
          background: 'linear-gradient(135deg, #198754 0%, #0d6efd 55%, #6610f2 100%)',
          color: '#fff',
          flexShrink: 0,
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: sc(32, s),
            right: sc(40, s),
            opacity: 0.25,
            fontSize: sc(160, s),
            lineHeight: 1,
          }}
          aria-hidden
        >
          📊
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: sc(16, s),
            fontSize: sc(40, s),
            fontWeight: 600,
            opacity: 0.95,
            marginBottom: sc(16, s),
          }}
        >
          <span style={{ fontSize: sc(44, s), lineHeight: 1 }} aria-hidden>
            ✈️
          </span>
          {planName}
        </div>
        <div style={{ fontSize: sc(64, s), fontWeight: 800, lineHeight: 1.08, maxWidth: '92%' }}>
          {dayTitle}
        </div>
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          padding: `${sc(40, s)}px ${sc(40, s)}px ${sc(48, s)}px`,
          display: 'flex',
          flexDirection: 'column',
          gap: sc(36, s),
          overflow: 'hidden',
        }}
      >
        <StatsSection
          sectionTitle={t('planDetail.days.shortsStats.today')}
          sectionEmoji="☀️"
          sectionColor="#0d6efd"
          stats={todayStats}
          tripTotalsMode={false}
          attractionCount={attractionCount}
          t={t}
          showAttractions
          compact={false}
          tripTotals={tripTotals}
          showTripSoFarParen={showTripParen}
          dense={false}
          s={s}
        />
      </div>
    </div>
  );
}

export default function DayShortsStatsModal({
  show,
  onHide,
  day,
  dayIndex,
  planName,
  allDays = [],
  onSaveStats,
}) {
  const { t } = useTranslation(undefined, { lng: 'en' });
  const captureRef = useRef(null);
  const [formStats, setFormStats] = useState({ ...EMPTY_SHORTS_STATS });
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const exportScale = SHORTS_WIDTH / PREVIEW_MAX_WIDTH;
  const previewHeight = Math.round(PREVIEW_MAX_WIDTH * (SHORTS_HEIGHT / SHORTS_WIDTH));

  const dayTitle = day?.date
    ? `Day ${dayIndex + 1}: ${format(new Date(day.date), 'PPP', { locale: enUS })}`
    : `Day ${dayIndex + 1}`;

  const todayStats = useMemo(() => normalizeShortsStats(formStats), [formStats]);

  const daysForTrip = useMemo(() => {
    if (!allDays?.length) return [];
    return allDays.map((d, i) => (i === dayIndex ? { ...d, shortsStats: todayStats } : d));
  }, [allDays, dayIndex, todayStats]);

  const tripTotals = useMemo(
    () => computeTripStatsTotals(daysForTrip, dayIndex),
    [daysForTrip, dayIndex]
  );

  const attractionCount = useMemo(() => countDayAttractions(day), [day]);

  useEffect(() => {
    if (!show) return;
    setFormStats(normalizeShortsStats(day?.shortsStats));
    setExportError('');
  }, [show, day?.id, day?.shortsStats]);

  const persistStats = useCallback(async () => {
    if (!day?.id || !onSaveStats) return;
    setIsSaving(true);
    try {
      await onSaveStats(day.id, todayStats);
    } finally {
      setIsSaving(false);
    }
  }, [day?.id, onSaveStats, todayStats]);

  const updateField = (field, value) => {
    setFormStats((prev) => ({ ...prev, [field]: value }));
  };

  const handleDownload = async () => {
    if (!captureRef.current) return;

    setIsExporting(true);
    setExportError('');

    try {
      await persistStats();
      await new Promise((resolve) => setTimeout(resolve, 300));

      const canvas = await html2canvas(captureRef.current, {
        scale: exportScale,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#e8eef5',
        logging: false,
      });

      const link = document.createElement('a');
      const safePlanName = (planName || 'trip').replace(/[^\w-]+/g, '-').replace(/-+/g, '-');
      link.download = `${safePlanName}-day-${dayIndex + 1}-shorts-stats.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Failed to export shorts stats:', error);
      setExportError(t('planDetail.days.shortsStats.exportFailed'));
    } finally {
      setIsExporting(false);
    }
  };

  const cardProps = {
    planName: planName || t('planDetail.overview.tripPlan'),
    dayTitle,
    totalDays: allDays.length || 1,
    todayStats,
    tripTotals,
    attractionCount,
    t,
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" fullscreen="lg-down">
      <Modal.Header closeButton dir="ltr" lang="en">
        <Modal.Title>
          <i className="bi bi-bar-chart-line me-2"></i>
          {t('planDetail.days.shortsStats.title')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body dir="ltr" lang="en">
        <p className="text-muted small">{t('planDetail.days.shortsStats.intro')}</p>
        <div className="row g-3">
          <div className="col-md-5">
            <Form>
              <Row className="g-2 mb-3">
                <Col xs={6}>
                  <Form.Group>
                    <Form.Label>
                      <i className="bi bi-person-walking me-1" />
                      {t('planDetail.days.shortsStats.steps')}
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      step={1}
                      value={formStats.steps ?? ''}
                      onChange={(e) => updateField('steps', e.target.value)}
                      placeholder="0"
                    />
                  </Form.Group>
                </Col>
                <Col xs={6}>
                  <Form.Group>
                    <Form.Label>
                      <i className="bi bi-signpost-2 me-1" />
                      {t('planDetail.days.shortsStats.km')}
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      step={0.1}
                      value={formStats.distanceKm ?? ''}
                      onChange={(e) => updateField('distanceKm', e.target.value)}
                      placeholder="0"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="g-2 mb-3">
                <Col xs={6}>
                  <Form.Group>
                    <Form.Label>
                      {getFunEmoji(formStats.fun != null && formStats.fun !== '' ? formStats.fun : null)}{' '}
                      {t('planDetail.days.shortsStats.fun')} (0–10)
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      max={10}
                      step={1}
                      value={formStats.fun ?? ''}
                      onChange={(e) => updateField('fun', e.target.value)}
                      placeholder="—"
                    />
                  </Form.Group>
                </Col>
                <Col xs={6}>
                  <Form.Group>
                    <Form.Label>
                      {getFatigueEmoji(
                        formStats.fatigue != null && formStats.fatigue !== '' ? formStats.fatigue : null
                      )}{' '}
                      {t('planDetail.days.shortsStats.fatigue')} (0–10)
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      max={10}
                      step={1}
                      value={formStats.fatigue ?? ''}
                      onChange={(e) => updateField('fatigue', e.target.value)}
                      placeholder="—"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Button variant="outline-primary" size="sm" onClick={persistStats} disabled={isSaving}>
                {isSaving ? t('planDetail.days.shortsStats.saving') : t('planDetail.days.shortsStats.save')}
              </Button>
            </Form>
          </div>
          <div className="col-md-7">
            <h6 className="mb-2">{t('planDetail.days.shortsStats.preview')}</h6>
            <p className="text-muted small mb-2">{t('planDetail.days.shortsStats.aspectRatioHelp')}</p>
            <div
              className="mx-auto border rounded overflow-hidden shadow"
              style={{
                width: PREVIEW_MAX_WIDTH,
                height: previewHeight,
                background: '#dee2e6',
              }}
            >
              <StatsCard {...cardProps} width={PREVIEW_MAX_WIDTH} frameRef={captureRef} />
            </div>
          </div>
        </div>
        {exportError && (
          <Alert variant="danger" className="mt-3 mb-0">
            {exportError}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer dir="ltr" lang="en">
        <Button variant="outline-secondary" onClick={onHide}>
          {t('common.close', { defaultValue: 'Close' })}
        </Button>
        <Button variant="primary" onClick={handleDownload} disabled={isExporting}>
          <i className="bi bi-download me-2"></i>
          {isExporting ? t('planDetail.days.shortsStats.downloading') : t('planDetail.days.shortsStats.download')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
